import React from 'react';
import { FiUsers, FiClock, FiZap, FiBarChart2, FiMail, FiCalendar, FiAward, FiGlobe } from 'react-icons/fi';

const SchoolAdmissions = () => {
  const features = [
    {
      icon: <FiGlobe className="w-6 h-6" />,
      title: 'Online Inquiry and Application',
      description: 'Parents can inquire about admissions online, schedule school tours, and submit applications from anywhere. No more visiting the school multiple times just to collect forms or submit documents.',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiUsers className="w-6 h-6" />,
      title: 'Parent-Friendly Application Process',
      description: 'School admissions involve parents, not just students. The application process must be simple, intuitive, and accessible for parents who may not be tech-savvy. Clear instructions and support are essential.',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiCalendar className="w-6 h-6" />,
      title: 'Admission Event Scheduling',
      description: 'Schools conduct admission events—open houses, parent interviews, student assessments, and school tours. The system should manage event scheduling, send reminders, and track attendance.',
      color: 'bg-amber-50'
    },
    {
      icon: <FiMail className="w-6 h-6" />,
      title: 'Automated Parent Communication',
      description: 'Parents want regular updates throughout the admission process. Automated emails and SMS keep them informed—application received, documents needed, event reminders, admission offer, enrollment steps.',
      color: 'bg-rose-50'
    },
    {
      icon: <FiClock className="w-6 h-6" />,
      title: 'Document Management',
      description: 'School admissions require multiple documents—birth certificates, address proofs, previous school records, and more. The system should make it easy for parents to upload, and for schools to verify.',
      color: 'bg-purple-50'
    },
    {
      icon: <FiBarChart2 className="w-6 h-6" />,
      title: 'Admission Analytics',
      description: 'Track application trends, conversion rates, parent preferences, and enrollment patterns. Use data to optimize your admission strategy and fill seats faster.',
      color: 'bg-blue-50'
    }
  ];

  const benefits = [
    {
      title: 'Reach More Families',
      description: 'Digital admissions extend your reach beyond your local area. Families from different locations can apply.',
      icon: <FiGlobe className="w-5 h-5" />
    },
    {
      title: 'Fill Seats Faster',
      description: 'Automated processes reduce admission timelines. Families get decisions faster, and schools fill seats sooner.',
      icon: <FiZap className="w-5 h-5" />
    },
    {
      title: 'Better Parent Experience',
      description: 'A smooth, transparent admission process creates a positive first impression. Parents remember the experience.',
      icon: <FiUsers className="w-5 h-5" />
    },
    {
      title: 'Reduce Administrative Work',
      description: 'Automate form processing, document verification, and communication. Free up staff for more important work.',
      icon: <FiClock className="w-5 h-5" />
    },
    {
      title: 'Build Your Brand',
      description: 'A modern admission process reflects well on your school. It signals quality, innovation, and care.',
      icon: <FiAward className="w-5 h-5" />
    },
    {
      title: 'Data-Driven Decisions',
      description: 'Understand which programs are most popular, which marketing channels work best, and how to improve.',
      icon: <FiBarChart2 className="w-5 h-5" />
    }
  ];

  const challenges = [
    {
      title: 'Complex Parent Needs',
      description: 'Parents have different needs—some prefer digital, others want personal attention. The system should support both.'
    },
    {
      title: 'Document Verification',
      description: 'Verifying documents like birth certificates and address proofs is time-consuming. Automated validation helps.'
    },
    {
      title: 'Event Coordination',
      description: 'Open houses, tours, and interviews need careful scheduling. Multiple families, different times, various staff.'
    },
    {
      title: 'Managing Waitlists',
      description: 'Schools maintain waitlists when seats are limited. Managing waitlists manually is chaotic and error-prone.'
    },
    {
      title: 'Parent Communication',
      description: 'Parents expect clear, regular communication. Any gap leads to anxiety and sometimes lost enrollments.'
    },
    {
      title: 'Competition from Other Schools',
      description: 'Private schools are competing for the same families. Speed and experience are competitive advantages.'
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        School admissions in India are a significant milestone for families. It's when parents choose the institution that will shape their child's future. For private schools, this is also a critical time—when they build their community, fill their classrooms, and set the foundation for the academic year.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        The school admission process is different from college admissions. <strong className="text-slate-900">Parents are heavily involved, decisions are emotional, and families visit campuses before making decisions.</strong> The process requires a mix of digital efficiency and personal touch.
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        Here's how private schools are using digital tools to enroll students faster while maintaining a warm, welcoming experience.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">60%</div>
          <div className="text-sm text-slate-500">Faster enrollment with digital admissions</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-emerald-600">80%</div>
          <div className="text-sm text-slate-500">Parents prefer online applications</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">3x</div>
          <div className="text-sm text-slate-500">Higher enrollment with digital processes</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">Key Features of Digital School Admissions</h2>

      {/* Features List */}
      {features.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm">{item.description}</p>
            </div>
          </div>
        </div>
      ))}

      {/* Benefits Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-3">What Digital School Admissions Deliver</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {benefits.map((item, i) => (
            <div key={i} className="bg-indigo-50 rounded-xl p-4 border border-indigo-200 flex items-start gap-3">
              <div className="bg-white rounded-lg p-2 flex-shrink-0">
                {item.icon}
              </div>
              <div>
                <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                <p className="text-xs text-slate-600">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Challenges Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Challenges in School Admissions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {challenges.map((item, i) => (
            <div key={i} className="bg-amber-50 rounded-xl p-4 border border-amber-200">
              <div className="flex items-start gap-2">
                <span className="text-amber-500 font-bold text-sm">⚠️</span>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-slate-600 mt-1">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Comparison Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Traditional vs. Digital School Admissions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <div className="text-sm font-semibold text-red-700 mb-2">Traditional Admissions</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Parents visit school multiple times</span></li>
              <li className="flex items-start gap-2">• <span>Paper forms and manual processing</span></li>
              <li className="flex items-start gap-2">• <span>No status visibility</span></li>
              <li className="flex items-start gap-2">• <span>Slow communication</span></li>
              <li className="flex items-start gap-2">• <span>Missed follow-ups</span></li>
            </ul>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="text-sm font-semibold text-emerald-700 mb-2">Digital Admissions with Truyon</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Apply online from anywhere</span></li>
              <li className="flex items-start gap-2">• <span>Automated form processing</span></li>
              <li className="flex items-start gap-2">• <span>Real-time status tracking</span></li>
              <li className="flex items-start gap-2">• <span>Automated communication</span></li>
              <li className="flex items-start gap-2">• <span>No missed opportunities</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Why Truyon Section */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">Why Truyon is the Best Choice for School Admissions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📝</div>
            <h4 className="font-bold text-slate-900 text-sm">Parent-Friendly Forms</h4>
            <p className="text-xs text-slate-600">Simple, intuitive applications for all parents</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📄</div>
            <h4 className="font-bold text-slate-900 text-sm">Document Management</h4>
            <p className="text-xs text-slate-600">Easy upload and verification of documents</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📅</div>
            <h4 className="font-bold text-slate-900 text-sm">Event Scheduling</h4>
            <p className="text-xs text-slate-600">Manage open houses, tours, and interviews</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">💬</div>
            <h4 className="font-bold text-slate-900 text-sm">Parent Communication</h4>
            <p className="text-xs text-slate-600">Automated updates and reminders</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📊</div>
            <h4 className="font-bold text-slate-900 text-sm">Waitlist Management</h4>
            <p className="text-xs text-slate-600">Organized waitlist tracking and management</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📱</div>
            <h4 className="font-bold text-slate-900 text-sm">Mobile-Friendly</h4>
            <p className="text-xs text-slate-600">Apply from any device, anywhere</p>
          </div>
        </div>
      </div>

      {/* The Parent Journey */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-3">The Modern Parent Admission Journey</h3>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div className="bg-indigo-50 rounded-xl p-3 text-center border border-indigo-200">
            <div className="text-2xl mb-1">🔍</div>
            <h4 className="font-bold text-slate-900 text-xs">Discover</h4>
            <p className="text-xs text-slate-600">Find school online</p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-3 text-center border border-emerald-200">
            <div className="text-2xl mb-1">📝</div>
            <h4 className="font-bold text-slate-900 text-xs">Apply</h4>
            <p className="text-xs text-slate-600">Submit application online</p>
          </div>
          <div className="bg-amber-50 rounded-xl p-3 text-center border border-amber-200">
            <div className="text-2xl mb-1">📄</div>
            <h4 className="font-bold text-slate-900 text-xs">Verify</h4>
            <p className="text-xs text-slate-600">Upload documents</p>
          </div>
          <div className="bg-rose-50 rounded-xl p-3 text-center border border-rose-200">
            <div className="text-2xl mb-1">🎉</div>
            <h4 className="font-bold text-slate-900 text-xs">Enroll</h4>
            <p className="text-xs text-slate-600">Receive admission offer</p>
          </div>
        </div>
      </div>

      {/* The Parent Perspective */}
      <div className="mt-8 bg-emerald-50 rounded-2xl p-6 border border-emerald-200">
        <h3 className="text-lg font-bold text-emerald-900 mb-3">What Parents Want in School Admissions</h3>
        <ul className="text-emerald-800 leading-relaxed space-y-2">
          <li className="flex items-start gap-2">• <span>Simple, clear application process</span></li>
          <li className="flex items-start gap-2">• <span>Regular updates on application status</span></li>
          <li className="flex items-start gap-2">• <span>Clear communication on next steps</span></li>
          <li className="flex items-start gap-2">• <span>Opportunity to visit campus and meet staff</span></li>
          <li className="flex items-start gap-2">• <span>Support for document submission</span></li>
          <li className="flex items-start gap-2">• <span>Quick decisions and enrollment confirmation</span></li>
        </ul>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">Enroll Students Faster with Truyon</h3>
        <p className="text-indigo-800 leading-relaxed">
          School admissions are the gateway to your school community. A smooth, welcoming admission process sets the tone for the entire parent-school relationship. It signals quality, care, and professionalism.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Truyon's digital school admission system makes it easy for parents to apply, simple for your staff to process applications, and fast for you to fill seats. Online applications, document management, event scheduling, automated communication—all in one platform.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Truyon for school admissions.</strong> Built for private schools. Built for parents. Ready to help you enroll faster.
        </p>
      </div>
    </>
  );
};

export default SchoolAdmissions;